import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
  HomeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  learnApi,
  triggerBlobDownload,
  type ChapterSummary,
  type ChapterDocumentKind,
} from '../api/learnApi';
import { usePageTitle } from '../hooks/usePageTitle';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useOutsideDismiss } from '../hooks/useOutsideDismiss';
import { StudySidebar } from '../components/learn/StudySidebar';
import { ChapterPicker } from '../components/learn/ChapterPicker';
import { paginate } from '../utils/chapterPagination';
import '../styles/learn-chapter.css';
import '../styles/study.css';

type LoadStatus = 'loading' | 'ready' | 'not-available' | 'error';

/** Read a 1-based `?page=` deep link (feature 022 search results) into a 0-based page index. */
function readPageParam(raw: string | null): number {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) - 1 : 0;
}

/**
 * Feature 021 — reads one Bridge-book chapter. Renders the authenticated HTML inside a viewport-
 * locked reader with a sticky header (Back / chapter title / Download), a sticky copyright footer,
 * and a First/Prev/numbers/Next/Last pager. The chapter is split into a small, device-independent
 * set of pages at hand/heading boundaries; each page scrolls vertically if it exceeds the screen.
 * The back control returns to whichever page linked here — see `backTo` below (FR-009).
 */
export function ChapterPage() {
  const { chapterSlug = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Feature 022 — a search result links here as `?page=N#b-sec-M`. Both are entry hints, consumed
  // once on load: the learner must stay free to page and scroll away afterwards without the deep
  // link yanking them back, so they are read into refs rather than tracked as live state.
  const initialPageRef = useRef(readPageParam(searchParams.get('page')));
  const pendingAnchorRef = useRef(location.hash ? location.hash.slice(1) : '');
  // Search results open the reader in a new tab and mark it `from=search`, which swaps the header's
  // Back control for Close (this tab has nothing to go "back" to).
  const openedFromSearch = searchParams.get('from') === 'search';

  const [html, setHtml] = useState<string | null>(null);
  // The whole manifest, not just this chapter: it labels the reader AND fills the tab rail.
  const [chapters, setChapters] = useState<ChapterSummary[] | null>(null);
  const [chaptersFailed, setChaptersFailed] = useState(false);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [downloading, setDownloading] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [page, setPage] = useState(initialPageRef.current); // 0-based
  // On phones the full numbered window (first … n-1 n n+1 … last) is too wide and wraps, so we show
  // only the current page there; First/Last (« ») still jump to the ends.
  const [narrow, setNarrow] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const downloadTriggerRef = useRef<HTMLButtonElement>(null);
  const downloadRootRef = useRef<HTMLDivElement>(null);

  const pages = useMemo(() => (html === null ? [] : paginate(html)), [html]);
  const pageCount = Math.max(1, pages.length);

  const meta = chapters?.find((c) => c.slug === chapterSlug) ?? null;
  const label = meta ? `Chapter ${meta.order} - ${meta.title}` : 'Learn';
  usePageTitle(`${label} — Hawkes Bridge`);
  const year = new Date().getFullYear();

  // --- Chapter manifest -------------------------------------------------------------------------
  // Fetched once rather than per chapter: with the tab rail, switching chapters is a routine move,
  // and the manifest is the same for all of them. A failure only costs the rail and the title
  // (the label falls back to "Learn"), never the chapter itself.
  useEffect(() => {
    let active = true;
    learnApi
      .getChapters()
      .then((list) => { if (active) setChapters(list); })
      .catch(() => { if (active) setChaptersFailed(true); });
    return () => { active = false; };
  }, []);

  // --- Content load -----------------------------------------------------------------------------
  useEffect(() => {
    let active = true;
    setStatus('loading');
    setHtml(null);
    setPage(initialPageRef.current); // clamped once the chapter paginates

    learnApi
      .getChapterContent(chapterSlug)
      .then((content) => { if (!active) return; setHtml(content); setStatus('ready'); })
      .catch((err) => {
        if (!active) return;
        setStatus(err?.response?.status === 404 ? 'not-available' : 'error');
      });

    return () => { active = false; };
  }, [chapterSlug]);

  // --- Size the reader to the space below the site Header so the bars stay pinned. --------------
  const recompute = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const top = root.getBoundingClientRect().top;
    root.style.height = `${Math.max(240, window.innerHeight - top)}px`;
  }, []);

  useEffect(() => {
    const onResize = () => {
      recompute();
      setNarrow(window.innerWidth <= 520); // matches the CSS phone breakpoint
    };
    onResize();
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [recompute, status]);

  // Clamp the current page once the chapter has paginated. Guarded on pages.length: before the
  // content loads pageCount is a placeholder 1, which would clamp an incoming `?page=` deep link
  // straight back to page 1.
  useEffect(() => {
    if (pages.length === 0) return;
    setPage((p) => Math.min(p, pages.length - 1));
  }, [pages.length]);

  // Each new page starts at the top, except on arrival via a `#b-sec-N` section deep link, where we
  // scroll that heading into view instead. The anchor is consumed on first use so paging onwards
  // behaves normally.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || pages.length === 0) return;

    const anchor = pendingAnchorRef.current;
    if (anchor) {
      pendingAnchorRef.current = '';
      const target = viewport.querySelector(`[id="${CSS.escape(anchor)}"]`);
      if (target) {
        target.scrollIntoView({ block: 'start' });
        return;
      }
    }
    viewport.scrollTop = 0;
  }, [page, pages]);

  // --- Navigation -------------------------------------------------------------------------------
  const goTo = useCallback((next: number) => {
    setPage((prev) => {
      const clamped = Math.min(Math.max(next, 0), pageCount - 1);
      return clamped === prev ? prev : clamped;
    });
  }, [pageCount]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goTo(page + 1);
      else if (e.key === 'ArrowLeft') goTo(page - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, page]);

  /**
   * Where Back returns to.
   *
   * Every in-app link into the reader stamps its own location into router state, so the
   * control returns the learner to the page they actually came from — the Learn list, or
   * the topic page whose "Study …" link they followed. Anything without that state (a
   * bookmark, a pasted URL, a hard refresh onto a fresh history entry) falls back to
   * `/learn`, which lists every chapter.
   *
   * This used to be a fixed `\dashboard?tab=learn&section=precision-notes`. Feature 023
   * re-homed the dashboard's Learn tab to `/learn`, which left the control pointing at a
   * tab that no longer exists — Back went to the dashboard from everywhere.
   */
  const navState = location.state as { from?: unknown; fromLabel?: unknown } | null;
  const backTo = typeof navState?.from === 'string' ? navState.from : '/learn';
  const backLabel = typeof navState?.fromLabel === 'string' ? navState.fromLabel : 'Learn';

  /**
   * Entry context decides whether this is the Study's own reader or a single chapter borrowed by
   * another page.
   *
   * Anywhere under `/learn` — the tab rail, the chapter list, a search result — is the Study, and
   * the whole book stays on screen beside the page. So is arriving with no origin at all (a
   * bookmark, a pasted URL), which has no other surface to belong to.
   *
   * A topic page's "Study <topic>" link stamps its own `/inventory/...` origin. That topic maps to
   * exactly ONE chapter, so browsing the other seven is not what was asked for: the rail goes, and
   * the control returns to the topic rather than offering the Study's home.
   */
  const showRail = typeof navState?.from !== 'string' || navState.from.startsWith('/learn');

  const handleHome = useCallback(() => {
    navigate('/learn');
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate(backTo);
  }, [navigate, backTo]);

  /**
   * Close the throwaway tab a search result opened; the browser then returns the learner to the tab
   * that spawned it, which still holds their results.
   *
   * We cannot focus the search tab ourselves: `target="_blank"` implies `rel="noopener"` in modern
   * browsers, so `window.opener` is null (verified). Closing is enough — the browser reactivates the
   * opener tab on its own.
   *
   * `window.close()` is only permitted while this tab's session history holds a single entry, which
   * is the normal case here but stops being true once the learner follows a link out and back (the
   * footer's Accessibility link, say). It fails silently, so fall back to the search page — where
   * Close promises to take them either way.
   */
  const handleClose = useCallback(() => {
    window.close();
    window.setTimeout(() => {
      if (!window.closed) navigate('/learn/search');
    }, 150);
  }, [navigate]);

  const closeDownloadMenu = useCallback(() => {
    setDownloadOpen(false);
    downloadTriggerRef.current?.focus();
  }, []);

  const dismissDownloadMenu = useCallback(() => setDownloadOpen(false), []);

  useEscapeClose(closeDownloadMenu, downloadOpen);
  // Tapping elsewhere dismisses the menu but leaves focus where the user put it — the same rule
  // the chapter picker beside it follows.
  useOutsideDismiss(downloadRootRef, dismissDownloadMenu, downloadOpen);

  const handleDownload = useCallback(async (kind: ChapterDocumentKind) => {
    setDownloadOpen(false);
    setDownloading(true);
    try {
      const { blob, filename } = await learnApi.downloadChapterDocument(chapterSlug, kind);
      triggerBlobDownload(blob, filename);
    } finally {
      setDownloading(false);
      downloadTriggerRef.current?.focus();
    }
  }, [chapterSlug]);

  // --- Styling helpers --------------------------------------------------------------------------
  const controlClasses =
    'inline-flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] px-3 py-2 rounded-md ' +
    'bg-cream hover:bg-cream border border-line text-ink focus:outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 disabled:cursor-default';

  // Full-width rows inside the download panel; 44px min-height keeps them within the
  // constitution's touch-target rule on phones.
  const downloadOptionClasses =
    'flex w-full items-center min-h-[44px] px-3 py-2 rounded-md text-left text-sm font-medium ' +
    'text-ink hover:bg-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

  const pagerBtn =
    'inline-flex items-center justify-center min-h-[40px] min-w-[40px] px-2 rounded-md border ' +
    'border-line bg-card hover:bg-cream text-ink focus:outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-40 disabled:cursor-default';

  // Red, so it reads as "this ends here" rather than as one more navigation control. White on
  // red-600 is 4.8:1 — above the 4.5:1 the constitution's WCAG AA rule requires.
  const closeClasses =
    'inline-flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] px-3 py-2 rounded-md ' +
    'bg-red-600 hover:bg-red-700 border border-red-700 text-white font-medium focus:outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1';

  const atFirst = page <= 0;
  const atLast = page >= pageCount - 1;

  /*
   * A FIXED-width pager: a sliding window of consecutive page numbers, clamped at both ends, so the
   * bar carries exactly the same number of controls in the same places however far through the
   * chapter the learner is.
   *
   * It used to be the `1 … n-1 n n+1 … last` form, whose control count grew and shrank as the
   * ellipses resolved (four controls on page 1, seven in the middle) — which slid the button out
   * from under the pointer that had just used it. Nothing is lost by dropping it: First and Last
   * still reach the ends in one click, and "Page X of Y" still states the position.
   *
   * Phones get three rather than seven, where seven wraps the bar onto a second line.
   */
  const windowSize = Math.min(narrow ? 3 : 7, pageCount);
  const windowStart = Math.min(
    Math.max(1, page + 1 - Math.floor(windowSize / 2)),
    pageCount - windowSize + 1,
  );
  const pageWindow = Array.from({ length: windowSize }, (_, i) => windowStart + i);

  // Sibling chapters opened from the rail inherit THIS reader's origin rather than pointing back at
  // the chapter just left, so Back keeps meaning "out of the Study", however many tabs were browsed.
  const railOrigin = openedFromSearch ? '/learn' : backTo;

  return (
    <div ref={rootRef} className={`study-body${showRail ? '' : ' study-body--solo'}`}>
      {showRail && (
        <StudySidebar
          chapters={chapters}
          failed={chaptersFailed}
          activeSlug={chapterSlug}
          originPath={railOrigin}
        />
      )}

      <div className="learn-reader">
      {/* Sticky header: Back/Close — chapter title — Chapters (phones) — Download */}
      <header className="learn-reader__bar learn-reader__bar--top">
        {/* Opened from a search result, this reader is a throwaway tab, and "Back" would be a lie —
            it navigates rather than returning the learner to their results. Offer Close instead. */}
        {openedFromSearch ? (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close this chapter and return to your search results"
            className={closeClasses}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            <span className="hidden sm:inline">Close</span>
          </button>
        ) : showRail ? (
          // Reading inside the Study: every chapter is already one click away in the rail, so the
          // control is not a "back" — it is the way out to the Study's own front page.
          <button type="button" onClick={handleHome} aria-label="Go to the Study home" className={controlClasses}>
            <HomeIcon className="h-5 w-5" aria-hidden="true" />
            <span className="hidden sm:inline">Home</span>
          </button>
        ) : (
          <button type="button" onClick={handleBack} aria-label={`Back to ${backLabel}`} className={controlClasses}>
            <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
            <span className="hidden sm:inline">Back</span>
          </button>
        )}

        <h1 className="learn-reader__title" title={label}>{label}</h1>

        {/* The tab rail's stand-in where there is no room for it (see study.css). Absent in the
            single-chapter reader for the same reason the rail is: that entry is about one chapter. */}
        {showRail && (
          <ChapterPicker
            chapters={chapters}
            failed={chaptersFailed}
            activeSlug={chapterSlug}
            originPath={railOrigin}
            triggerClassName={controlClasses}
          />
        )}

        {/* Download is a disclosure rather than a single button: the chapter ships as both a Word
            document and a PDF, and neither is the obvious default. The menu is a plain list of
            buttons — the WAI menu pattern's arrow-key roving focus buys nothing over Tab for two
            items, and getting it half-right is worse than not claiming the role. */}
        <div ref={downloadRootRef} className="relative">
          <button
            ref={downloadTriggerRef}
            type="button"
            onClick={() => setDownloadOpen((o) => !o)}
            disabled={status !== 'ready' || downloading}
            aria-expanded={downloadOpen}
            aria-controls="chapter-download-panel"
            aria-label="Download this chapter"
            data-testid="chapter-download"
            className={controlClasses}
          >
            <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
            <span className="hidden sm:inline">{downloading ? 'Downloading…' : 'Download'}</span>
          </button>

          {downloadOpen && (
            <div
              id="chapter-download-panel"
              data-testid="chapter-download-panel"
              // Right-aligned: this control sits at the end of the header bar, so a
              // left-aligned panel would overflow the viewport on phones.
              className="absolute right-0 z-30 mt-2 w-56 rounded-xl border border-line bg-card p-2 shadow-lg"
            >
              <button
                type="button"
                onClick={() => handleDownload('word')}
                data-testid="chapter-download-word"
                className={downloadOptionClasses}
              >
                Word document (.docx)
              </button>

              {meta?.hasPdf && (
                <button
                  type="button"
                  onClick={() => handleDownload('pdf')}
                  data-testid="chapter-download-pdf"
                  className={downloadOptionClasses}
                >
                  PDF (.pdf)
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Scrollable page viewport */}
      <div ref={viewportRef} className="learn-reader__viewport">
        {status === 'loading' && <p className="learn-reader__msg">Loading chapter…</p>}
        {status === 'not-available' && <p className="learn-reader__msg">This chapter is not available yet.</p>}
        {status === 'error' && (
          <p className="learn-reader__msg">Something went wrong loading this chapter. Please try again.</p>
        )}
        {status === 'ready' && pages.length > 0 && (
          // Trusted first-party content produced and content-verified by the in-house converter
          // (research R6); no user-generated HTML is ever rendered here.
          <article
            className="learn-chapter learn-reader__content"
            dangerouslySetInnerHTML={{ __html: pages[Math.min(page, pages.length - 1)] ?? '' }}
          />
        )}
      </div>

      {/* Pager */}
      {status === 'ready' && (
        <nav className="learn-reader__pager" aria-label="Chapter pages">
          <button type="button" className={pagerBtn} onClick={() => goTo(0)} disabled={atFirst} aria-label="First page">
            <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button type="button" className={pagerBtn} onClick={() => goTo(page - 1)} disabled={atFirst} aria-label="Previous page">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>

          <ul className="learn-reader__pages">
            {pageWindow.map((token) => (
              <li key={token}>
                <button
                  type="button"
                  className={`${pagerBtn} ${token === page + 1 ? 'learn-reader__page--current' : ''}`}
                  aria-label={`Page ${token}`}
                  aria-current={token === page + 1 ? 'page' : undefined}
                  onClick={() => goTo(token - 1)}
                >
                  {token}
                </button>
              </li>
            ))}
          </ul>

          <button type="button" className={pagerBtn} onClick={() => goTo(page + 1)} disabled={atLast} aria-label="Next page">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button type="button" className={pagerBtn} onClick={() => goTo(pageCount - 1)} disabled={atLast} aria-label="Last page">
            <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>

          <span className="learn-reader__count" aria-live="polite">Page {page + 1} of {pageCount}</span>
        </nav>
      )}

      {/* Sticky footer: copyright */}
      <footer className="learn-reader__bar learn-reader__bar--bottom">
        <p>
          &copy; {year} Hawkes Bridge. All rights reserved.
          {' · '}
          <Link to="/accessibility" className="text-primary-700 underline hover:text-primary-900">Accessibility</Link>
        </p>
      </footer>
      </div>
    </div>
  );
}
